import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { fiscalService } from '../../services/fiscalService';

// Lista fixa das 27 UFs - não depende de cadastro local (que hoje só tem 6 estados)
const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

export interface MunicipioUfValue {
  municipio: string;
  uf: string;
  codigoIbge: string;
}

interface MunicipioFocus {
  codigo: string;
  nome: string;
}

// Cache em memória por UF, evita rebater na Focus NFe a cada tecla/reabertura do modal
const cacheMunicipiosPorUf = new Map<string, MunicipioFocus[]>();

function normalizarMunicipios(raw: any): MunicipioFocus[] {
  const lista = Array.isArray(raw) ? raw : raw?.municipios || raw?.data || [];
  return lista
    .map((item: any) => ({
      codigo: String(item.codigo_municipio ?? item.codigo ?? item.ibge ?? ''),
      nome: String(item.municipio ?? item.nome ?? item.descricao ?? ''),
    }))
    .filter((m: MunicipioFocus) => m.codigo && m.nome);
}

async function buscarMunicipiosPorUf(uf: string): Promise<MunicipioFocus[]> {
  if (!uf) return [];
  const cacheKey = uf.toUpperCase();
  if (cacheMunicipiosPorUf.has(cacheKey)) return cacheMunicipiosPorUf.get(cacheKey)!;
  try {
    const raw = await fiscalService.consultarMunicipiosFocus(cacheKey);
    const lista = normalizarMunicipios(raw);
    cacheMunicipiosPorUf.set(cacheKey, lista);
    return lista;
  } catch (e) {
    console.warn('Erro ao consultar municípios na Focus NFe:', e);
    return [];
  }
}

interface MunicipioUfSelectProps {
  label: string;
  value: MunicipioUfValue;
  onChange: (v: MunicipioUfValue) => void;
  required?: boolean;
}

export function MunicipioUfSelect({ label, value, onChange, required }: MunicipioUfSelectProps) {
  const [opcoes, setOpcoes] = useState<MunicipioFocus[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [abertoDropdown, setAbertoDropdown] = useState(false);
  const [textoDigitado, setTextoDigitado] = useState(value.municipio || '');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTextoDigitado(value.municipio || '');
  }, [value.municipio]);

  useEffect(() => {
    if (!value.uf) {
      setOpcoes([]);
      return;
    }
    let cancelado = false;
    setCarregando(true);
    buscarMunicipiosPorUf(value.uf).then((lista) => {
      if (!cancelado) {
        setOpcoes(lista);
        setCarregando(false);
      }
    });
    return () => {
      cancelado = true;
    };
  }, [value.uf]);

  useEffect(() => {
    const handleClickFora = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setAbertoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, []);

  const sugestoes = textoDigitado
    ? opcoes.filter((m) => m.nome.toLowerCase().includes(textoDigitado.toLowerCase())).slice(0, 30)
    : opcoes.slice(0, 30);

  const handleSelecionar = (m: MunicipioFocus) => {
    setTextoDigitado(m.nome);
    setAbertoDropdown(false);
    onChange({ municipio: m.nome, uf: value.uf, codigoIbge: m.codigo });
  };

  return (
    <div className="grid grid-cols-3 gap-2" ref={containerRef}>
      <div className="col-span-2 relative">
        <label className="block text-2xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
          <MapPin className="w-3 h-3 text-slate-400" /> {label}
          {value.codigoIbge && (
            <span className="text-3xs font-mono text-emerald-600 font-bold ml-1">IBGE {value.codigoIbge}</span>
          )}
        </label>
        <input
          type="text"
          required={required}
          value={textoDigitado}
          onFocus={() => setAbertoDropdown(true)}
          onChange={(e) => {
            setTextoDigitado(e.target.value);
            setAbertoDropdown(true);
            // Enquanto o usuário digita sem selecionar da lista, zera o código IBGE
            // para não emitir com um código de outra cidade por engano.
            if (value.codigoIbge) onChange({ municipio: e.target.value, uf: value.uf, codigoIbge: '' });
          }}
          placeholder="Digite para buscar..."
          className={`w-full bg-white border rounded-lg px-3 py-1.5 text-xs ${
            value.codigoIbge ? 'border-emerald-300' : 'border-slate-300'
          }`}
        />
        {abertoDropdown && (
          <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
            {carregando ? (
              <div className="p-2.5 text-2xs text-slate-500 flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando municípios na Focus NFe...
              </div>
            ) : sugestoes.length > 0 ? (
              sugestoes.map((m) => (
                <button
                  type="button"
                  key={m.codigo}
                  onClick={() => handleSelecionar(m)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 flex items-center justify-between"
                >
                  <span>{m.nome}</span>
                  <span className="text-3xs font-mono text-slate-400">{m.codigo}</span>
                </button>
              ))
            ) : (
              <div className="p-2.5 text-2xs text-slate-400">
                {value.uf ? 'Nenhum município encontrado.' : 'Selecione a UF primeiro.'}
              </div>
            )}
          </div>
        )}
      </div>
      <div>
        <label className="block text-2xs font-semibold text-slate-600 mb-1">UF</label>
        <select
          required={required}
          value={value.uf}
          onChange={(e) => onChange({ municipio: '', uf: e.target.value, codigoIbge: '' })}
          className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs font-mono uppercase"
        >
          <option value="">--</option>
          {UFS.map((uf) => (
            <option key={uf} value={uf}>{uf}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
